import React, { useState } from 'react';
import styled from '@emotion/styled';
import {
  DashboardAudit,
  DashboardAuditSection,
  DashboardSectionRow,
} from '../models/dashboard';

// Weekly dashboard (GRI-957): one audit of a location report, as in the design prototype
// (AuditSection): pass rate against the goal, a figure per issue type, the sample photos with
// filters, and the share of failed samples by day and shift.

type Props = {
  section: DashboardAuditSection;
  audit?: DashboardAudit; // the audit's line in the published week: pass rate, goal, category
  weekStart: string;
};

const PER_PAGE = 16;
const BUBBLE_MAX = 44;
const SHIFTS = [
  {
    label: 'Lunch',
    hours: 'Before 4 PM',
    has: (r: DashboardSectionRow) => r.hour < 16,
  },
  {
    label: 'Dinner',
    hours: 'From 4 PM',
    has: (r: DashboardSectionRow) => r.hour >= 16,
  },
];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const weekDays = (weekStart: string) =>
  Array.from({ length: 7 }, (_, i) => {
    const d = new Date(`${weekStart}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
const dayLabel = (d: string) =>
  `${DAY_NAMES[new Date(`${d}T12:00:00Z`).getUTCDay()]} ${d.slice(8, 10)}`;
const pctFailed = (rows: DashboardSectionRow[]) =>
  rows.length
    ? (rows.filter((r) => !r.passed).length / rows.length) * 100
    : null;

type Cell = { shift: number; day: string }; // day '' = whole week

const AuditSection = ({ section, audit, weekStart }: Props) => {
  const [verdict, setVerdict] = useState<'all' | 'fail' | 'pass'>('all');
  const [criterion, setCriterion] = useState('');
  const [cell, setCell] = useState<Cell | null>(null);
  const [page, setPage] = useState(0);
  const [showImages, setShowImages] = useState(true);

  const { rows } = section;
  const days = weekDays(weekStart);
  const passRate =
    audit?.passRate ??
    Math.round((rows.filter((r) => r.passed).length / rows.length) * 1000) / 10;
  const goal = audit?.threshold ?? null;
  // Issue types, most frequent first.
  const counts = new Map<string, number>();
  rows.forEach((r) =>
    r.issues.forEach((i) => counts.set(i, (counts.get(i) || 0) + 1))
  );
  const criteria = Array.from(counts.keys()).sort(
    (a, b) => counts.get(b)! - counts.get(a)!
  );

  const inCell = (r: DashboardSectionRow, c: Cell) =>
    SHIFTS[c.shift].has(r) && (!c.day || r.date === c.day);
  const filtered = rows.filter((r) => {
    if (cell && !inCell(r, cell)) return false;
    const ok = criterion ? !r.issues.includes(criterion) : r.passed;
    return verdict === 'all' || (verdict === 'pass') === ok;
  });
  const pages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, pages - 1);
  const shown = filtered.slice(current * PER_PAGE, (current + 1) * PER_PAGE);

  const reset = () => {
    setCell(null);
    setVerdict('all');
    setCriterion('');
    setPage(0);
  };
  const pickCell = (c: Cell) => {
    if (cell && cell.shift === c.shift && cell.day === c.day) return reset();
    setCell(c);
    setVerdict('fail');
    setPage(0);
  };
  // cells[shift][day], the last one the whole week.
  const cells = SHIFTS.map((s, si) =>
    [...days, ''].map((day) => ({
      shift: si,
      day,
      p: pctFailed(rows.filter((r) => inCell(r, { shift: si, day }))),
      title: `${s.label}, ${day ? dayLabel(day) : 'whole week'}`,
    }))
  );
  const worst = (items: { label: string; p: number | null }[]) =>
    items
      .filter((i) => i.p !== null)
      .sort((a, b) => (b.p as number) - (a.p as number))[0];
  const worstDay = worst(
    days.map((d) => ({
      label: dayLabel(d),
      p: pctFailed(rows.filter((r) => r.date === d)),
    }))
  );
  const worstShift = worst(
    SHIFTS.map((s) => ({ label: s.label, p: pctFailed(rows.filter(s.has)) }))
  );
  const worstLabel = (w?: { label: string; p: number | null }) =>
    w ? `${w.label} (${Math.round(w.p as number)}% failed)` : '—';

  const bubble = (c: typeof cells[0][0]) => {
    if (c.p === null) return <span className="none">—</span>;
    const on = !!cell && cell.shift === c.shift && cell.day === c.day;
    const size = Math.max(6, Math.round(BUBBLE_MAX * Math.sqrt(c.p / 100)));
    return (
      <button
        className="cell"
        aria-pressed={on}
        title={`${c.title}: ${c.p.toFixed(1)}% failed. Click to filter.`}
        onClick={() => pickCell(c)}
      >
        <span
          className={on ? 'bubble on' : 'bubble'}
          style={{ width: size, height: size }}
        />
        <span>{Math.round(c.p)}%</span>
      </button>
    );
  };
  const failLabel = criterion ? `Fail ${criterion.toLowerCase()}` : 'Failed';
  const passLabel = criterion ? `Pass ${criterion.toLowerCase()}` : 'Passed';

  return (
    <Wrap id={`audit-${section.auditId}`}>
      <div className="head">
        <div>
          {audit?.category && <div className="eyebrow">{audit.category}</div>}
          <h2>{section.name || audit?.name || `Audit ${section.auditId}`}</h2>
        </div>
        {section.sopUrl && (
          <a
            className="sop"
            href={section.sopUrl}
            target="_blank"
            rel="noreferrer"
          >
            View SOP
          </a>
        )}
      </div>

      <section className="stats">
        <div className="stat">
          <p className="label">Pass rate</p>
          <p className="value">{passRate.toFixed(1)}%</p>
          <div className="bar">
            <div style={{ width: `${Math.min(100, passRate)}%` }} />
            {goal !== null && <i style={{ left: `${goal}%` }} />}
          </div>
          {goal !== null && (
            <p className="note">
              {passRate >= goal
                ? `Goal of ${goal}% reached`
                : `${
                    Math.round((goal - passRate) * 10) / 10
                  } points below the ${goal}% goal`}
            </p>
          )}
        </div>
        {criteria.length >= 2 &&
          criteria.map((c) => (
            <div className="stat criterion" key={c}>
              <p className="label">{c}</p>
              <p className="value">
                {(
                  (rows.filter((r) => !r.issues.includes(c)).length /
                    rows.length) *
                  100
                ).toFixed(1)}
                %
              </p>
              <p className="note">Samples without this issue</p>
            </div>
          ))}
      </section>

      <section className="gallery">
        <div className="bar-head">
          <h3>Samples this week</h3>
          <div className="filters">
            {cell && (
              <button
                className="chip"
                title="Clear day and shift filter"
                onClick={reset}
              >
                {SHIFTS[cell.shift].label},{' '}
                {cell.day ? dayLabel(cell.day) : 'whole week'} ✕
              </button>
            )}
            <div className="segments" role="group" aria-label="Result">
              {(
                [
                  ['all', 'All'],
                  ['fail', failLabel],
                  ['pass', passLabel],
                ] as const
              ).map(([v, label]) => (
                <button
                  key={v}
                  aria-pressed={verdict === v}
                  onClick={() => {
                    setVerdict(v);
                    setPage(0);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {criteria.length >= 2 && (
              <select
                aria-label="Criterion"
                value={criterion}
                onChange={(e) => {
                  setCriterion(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">All criteria</option>
                {criteria.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            )}
            <button className="link" onClick={() => setShowImages(!showImages)}>
              {showImages ? 'Hide images' : 'Show images'}
            </button>
          </div>
        </div>
        {showImages ? (
          <>
            {filtered.length === 0 ? (
              <p className="empty">No samples match this selection.</p>
            ) : (
              <div className="grid">
                {shown.map((r, i) => (
                  <figure key={current * PER_PAGE + i}>
                    {r.photoUrl ? (
                      <a href={r.photoUrl} target="_blank" rel="noreferrer">
                        <img src={r.photoUrl} alt={r.time} loading="lazy" />
                      </a>
                    ) : (
                      <div className="nophoto">No photo</div>
                    )}
                    <figcaption>
                      <span className={r.passed ? 'pass' : 'fail'}>
                        {r.passed ? 'Passed' : 'Failed'}
                      </span>
                      {r.issues.length > 0 && ` — ${r.issues.join(', ')}`}
                      <span className="time">{r.time}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
            {filtered.length > 0 && (
              <div className="pager">
                <button
                  className="link"
                  disabled={current === 0}
                  onClick={() => setPage(current - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {current + 1} of {pages}
                </span>
                <button
                  className="link"
                  disabled={current + 1 >= pages}
                  onClick={() => setPage(current + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="empty">Images hidden</p>
        )}
      </section>

      <section className="distribution">
        <h3>Distribution</h3>
        <p className="note">Share of failed samples by day and shift.</p>
        {/* Desktop: a row per shift. Phones: a row per day. */}
        <div className="d-only scroll">
          <table>
            <thead>
              <tr>
                <th />
                {days.map((d) => (
                  <th key={d}>{dayLabel(d)}</th>
                ))}
                <th className="week">Week</th>
              </tr>
            </thead>
            <tbody>
              {SHIFTS.map((s, si) => (
                <tr key={s.label}>
                  <th className="shift">
                    {s.label}
                    <span>{s.hours}</span>
                  </th>
                  {cells[si].map((c) => (
                    <td key={c.day} className={c.day ? '' : 'week'}>
                      {bubble(c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <table className="m-only">
          <thead>
            <tr>
              <th />
              {SHIFTS.map((s) => (
                <th key={s.label} className="shift">
                  {s.label}
                  <span>{s.hours}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...days, ''].map((d, j) => (
              <tr key={d} className={d ? '' : 'week-row'}>
                <th>{d ? dayLabel(d) : 'Week'}</th>
                {SHIFTS.map((s, si) => (
                  <td key={s.label}>{bubble(cells[si][j])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <dl>
          <div>
            <dt>Day with most failures</dt>
            <dd>{worstLabel(worstDay)}</dd>
          </div>
          <div>
            <dt>Shift with most failures</dt>
            <dd>{worstLabel(worstShift)}</dd>
          </div>
        </dl>
      </section>
    </Wrap>
  );
};

const NAVY = '#325882';
const FAIL = '#BA1A1A';
const MUTED = '#43474E';
const LINE = '#C3C6CF';
const SURFACE = '#E1E2E8';

const Wrap = styled.article`
  margin-top: 64px;
  padding-top: 40px;
  border-top: 4px solid ${SURFACE};
  scroll-margin-top: 72px;
  color: #191c20;
  font-size: 14px;
  button {
    font: inherit;
    cursor: pointer;
  }
  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 24px;
  }
  .eyebrow,
  .label {
    margin: 0 0 4px;
    font-weight: 500;
    color: ${MUTED};
  }
  h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 400;
    line-height: 28px;
  }
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
  }
  .sop {
    flex: none;
    border: 1px solid #73777f;
    border-radius: 8px;
    padding: 8px 12px;
    color: ${NAVY};
    font-size: 12px;
    font-weight: 500;
    text-decoration: none;
  }
  .stats {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    border-top: 1px solid ${LINE};
    border-bottom: 1px solid ${LINE};
  }
  .stat {
    padding: 24px 32px 24px 0;
  }
  .stat.criterion {
    padding-left: 32px;
    border-left: 1px solid ${LINE};
  }
  .value {
    margin: 8px 0 0;
    font-size: 24px;
    font-weight: 800;
    color: ${NAVY};
  }
  .note {
    margin: 12px 0 0;
    color: ${MUTED};
  }
  .bar {
    position: relative;
    height: 10px;
    margin-top: 16px;
    background: ${SURFACE};
    border-radius: 999px;
    div {
      height: 100%;
      border-radius: 999px;
      background: ${NAVY};
    }
    i {
      position: absolute;
      top: -6px;
      bottom: -6px;
      width: 2px;
      background: #6f5d00;
    }
  }
  .gallery,
  .distribution {
    padding-top: 40px;
  }
  .bar-head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${LINE};
  }
  .filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 24px;
  }
  .chip,
  select {
    border: none;
    background: ${SURFACE};
    border-radius: 8px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
  }
  .segments {
    display: inline-flex;
    background: ${SURFACE};
    border-radius: 8px;
    padding: 2px;
    button {
      border: none;
      border-radius: 6px;
      padding: 6px 12px;
      font-size: 12px;
      font-weight: 500;
      background: transparent;
      color: ${MUTED};
    }
    button[aria-pressed='true'] {
      background: #ffffff;
      color: #191c20;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    }
  }
  .link {
    border: none;
    background: none;
    padding: 0;
    font-size: 12px;
    font-weight: 500;
    color: ${NAVY};
  }
  .link:disabled {
    color: rgba(25, 28, 32, 0.38);
    cursor: default;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
    gap: 24px 16px;
    padding: 24px 0;
  }
  figure {
    margin: 0;
    min-width: 0;
  }
  img,
  .nophoto {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    background: ${SURFACE};
    color: #73777f;
    font-size: 12px;
  }
  figcaption {
    margin-top: 8px;
    font-size: 12px;
    line-height: 20px;
    color: ${MUTED};
  }
  .pass {
    font-weight: 500;
    color: #191c20;
  }
  .fail {
    font-weight: 500;
    color: ${FAIL};
  }
  .time {
    display: block;
  }
  .empty {
    margin: 0;
    padding: 32px 0;
    text-align: center;
    color: ${MUTED};
  }
  .pager {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: ${MUTED};
  }
  .distribution > .note {
    margin: 4px 0 0;
    padding-bottom: 16px;
    border-bottom: 1px solid ${LINE};
  }
  table {
    width: 100%;
    margin-top: 16px;
    border-collapse: collapse;
  }
  .scroll {
    overflow-x: auto;
  }
  .scroll table {
    min-width: 560px;
  }
  th {
    font-weight: 400;
    color: ${MUTED};
    padding: 0 0 12px;
  }
  tbody th {
    text-align: left;
    padding: 8px 0;
  }
  tr + tr,
  tbody tr {
    border-top: 1px solid ${SURFACE};
  }
  .shift {
    color: ${NAVY};
    span {
      display: block;
      font-size: 12px;
      color: ${MUTED};
    }
  }
  td {
    text-align: center;
    vertical-align: middle;
    padding: 8px 0;
  }
  .week {
    padding-left: 16px;
    border-left: 1px solid ${LINE};
    font-weight: 500;
    color: ${NAVY};
  }
  tr.week-row {
    border-top: 1px solid ${LINE};
  }
  tr.week-row th {
    font-weight: 500;
    color: ${NAVY};
  }
  .cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    height: 66px;
    padding: 0;
    border: none;
    background: none;
    font-size: 12px;
    color: #191c20;
  }
  .bubble {
    display: block;
    margin-bottom: 4px;
    border-radius: 999px;
    background: ${FAIL};
    opacity: 0.85;
  }
  .bubble.on {
    box-shadow: 0 0 0 3px #ffffff, 0 0 0 5px ${NAVY};
  }
  .none {
    color: #73777f;
  }
  dl {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin: 24px 0 0;
    padding-top: 16px;
    border-top: 1px solid ${LINE};
  }
  dt {
    color: ${MUTED};
  }
  dd {
    margin: 4px 0 0;
    color: ${NAVY};
  }
  .m-only {
    display: none;
  }
  @media (max-width: 640px) {
    .stat.criterion {
      padding-left: 0;
      border-left: none;
      border-top: 1px solid ${LINE};
    }
    .d-only {
      display: none;
    }
    .m-only {
      display: table;
    }
  }
`;

export default AuditSection;
